@if (isOpen) {
  <div class="report-modal-overlay">
    <div class="report-modal-container" (appClickOutside)="onClose()">
      <!-- Header -->
      <div class="report-modal-header">
        <div class="header-title">
          <div class="icon-badge">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"
              ></path>
              <line x1="4" y1="22" x2="4" y2="15"></line>
            </svg>
          </div>
          <div class="title-text">
            <h3>{{ reportContent?.title ?? "-" }}</h3>
            <span class="subtitle"
              >Help us keep the portal accurate and safe</span
            >
          </div>
        </div>
        <button
          type="button"
          class="close-btn"
          (click)="onClose()"
          [disabled]="isSubmitting"
          title="Close modal"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <!-- Content Body -->
      <div class="report-modal-body" #modalBody>
        @if (reportContent && reportContent.itemTitle) {
          <div class="item-context-box">
            <span class="context-label">Reporting:</span>
            <span class="context-title" [title]="reportContent.itemTitle">{{
              reportContent.itemTitle
            }}</span>
          </div>
        }

        <div class="section-heading">Select a reason for flagging</div>

        <!-- Dynamic Reasons List -->
        <div class="reasons-list">
          @for (reason of reportReasons; track reason.id) {
            <label
              class="reason-option"
              [class.selected]="selectedReason?.code === reason.code"
            >
              <input
                type="radio"
                name="reportReason"
                [value]="reason"
                [checked]="selectedReason?.code === reason.code"
                [disabled]="isSubmitting"
                (change)="selectReason(reason)"
              />
              <span class="radio-custom"></span>
              <span class="reason-label">{{ reason.label }}</span>
            </label>
          }
        </div>

        <!-- Severity Selection -->
        <div class="severity-section">
          <label for="reportSeverity" class="severity-label">Severity</label>
          <select
            id="reportSeverity"
            class="severity-select"
            [(ngModel)]="selectedSeverity"
            [disabled]="isSubmitting"
            (change)="clearSubmitError()"
          >
            @for (option of severityOptions; track option.value) {
              <option [ngValue]="option.value">{{ option.label }}</option>
            }
          </select>
        </div>

        <!-- Optional Text Area -->
        <div class="details-section">
          <label for="reportDetails" class="details-label">
            Additional Details <span class="optional-tag">(Optional)</span>
          </label>
          <textarea
            id="reportDetails"
            class="details-textarea"
            rows="3"
            [(ngModel)]="details"
            [disabled]="isSubmitting"
            (input)="clearSubmitError()"
            placeholder="Provide any additional context or explanation..."
            maxlength="500"
          ></textarea>
          <div class="char-count">{{ details ? details.length : 0 }}/500</div>
        </div>

        <!-- Optional File Upload Section -->
        <div class="attachment-section">
          <div class="attachment-label">
            Attach Screenshots / Files
            <span class="optional-tag"
              >(Optional, Images & PDF only, Max 3 files, 5MB each)</span
            >
          </div>

          @if (attachedFiles.length < maxFiles) {
            <div
              class="file-dropzone"
              [class.dragging]="isDragging"
              [class.disabled]="isSubmitting"
              (dragover)="onDragOver($event)"
              (dragleave)="onDragLeave($event)"
              (drop)="onDrop($event)"
              (click)="!isSubmitting && fileInput.click()"
              (keyup.enter)="!isSubmitting && fileInput.click()"
              (keyup.space)="!isSubmitting && fileInput.click()"
              tabindex="0"
              role="button"
              aria-label="Upload screenshots or PDF files"
            >
              <input
                #fileInput
                type="file"
                multiple
                [accept]="acceptedFileTypes"
                class="hidden-file-input"
                [disabled]="isSubmitting"
                (change)="onFileSelected($event)"
              />
              <div class="dropzone-content">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="upload-icon"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <span class="upload-text"
                  >Click or drag images/PDF files to upload</span
                >
              </div>
            </div>
          }

          @if (fileError) {
            <div class="file-error-msg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="15"
                height="15"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{{ fileError }}</span>
            </div>
          }

          <!-- Attached Files List -->
          @if (attachedFiles.length > 0) {
            <div class="attached-files-list">
              @for (
                file of attachedFiles;
                let i = $index;
                track file.name + i
              ) {
                <div class="attached-file-chip">
                  <div class="file-info-chip">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="15"
                      height="15"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      class="file-chip-icon"
                    >
                      <path
                        d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"
                      ></path>
                      <polyline points="13 2 13 9 20 9"></polyline>
                    </svg>
                    <span class="file-chip-name" [title]="file.name">{{
                      file.name
                    }}</span>
                    <span class="file-chip-size"
                      >({{ formatFileSize(file.size) }})</span
                    >
                  </div>
                  <button
                    type="button"
                    class="remove-file-btn"
                    (click)="removeFile(i)"
                    [disabled]="isSubmitting"
                    title="Remove file"
                    aria-label="Remove file"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              }
            </div>
          }
        </div>

        @if (submitErrorMessage) {
          <div class="submit-error-banner">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{{ submitErrorMessage }}</span>
          </div>
        }
      </div>

      <!-- Modal Footer -->
      <div class="report-modal-footer">
        <button
          type="button"
          class="btn btn-secondary"
          (click)="onClose()"
          [disabled]="isSubmitting"
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          [disabled]="!selectedReason || isSubmitting"
          (click)="onSubmit()"
        >
          @if (isSubmitting) {
            <span class="spinner-small"></span>
            <span>Submitting...</span>
          } @else {
            <span>Submit Report</span>
          }
        </button>
      </div>
    </div>
  </div>
}
